import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layouts/MainLayout';
import apiClient from '../api/apiClient';
import Swal from 'sweetalert2';

function TaskHistory() {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Technical categories and languages (you might want to fetch these from an API)
  const technicalCategories = [
    'Frontend Development',
    'Backend Development',
    'Full Stack Development',
    'DevOps',
    'Mobile Development',
    'Data Science',
    'Machine Learning',
    'Other'
  ];

  const programmingLanguages = [
    'JavaScript',
    'Python',
    'Java',
    'Kotlin',
    'Scala',
    'Erlang',
    'Elixir',
    'Dart',
    'C++',
    'Ruby',
    'PHP',
    'Swift',
    'Go',
    'Rust',
    'TypeScript',
    'Other'
  ];

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      const response = await apiClient.get('/api/update');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load tasks',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update technical info
  const updateTechnicalInfo = async (taskId, language, category) => {
    setUpdating(true);
    try {
      await apiClient.put(`/api/tasks/update-technical-info/${taskId}/`, {
        programming_language: language,
        technical_category: category
      });
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Technical information updated successfully',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      fetchTasks(); // Refresh the tasks list
    } catch (error) {
      console.error('Error updating technical info:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update technical information',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  return (
    <MainLayout>
      <div className="p-4 md:ml-64 min-h-screen pt-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white mb-6">
              Tasks Technical History
            </h1>

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <div className="grid gap-6">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-all duration-200"
                  >
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {task.title}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {task.description}
                        </p>
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 mr-2">
                            Current Language: {task.programming_language || 'Not set'}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Current Category: {task.technical_category || 'Not set'}
                          </span>
                        </div>
                      </div>

                      {!task.is_technical_info_updated && (
                        <div className="flex flex-col gap-2 md:w-1/3">
                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            defaultValue={task.programming_language || ''}
                            onChange={(e) => {
                              const newLanguage = e.target.value;
                              const currentCategory = task.technical_category || technicalCategories[0];
                              updateTechnicalInfo(task.id, newLanguage, currentCategory);
                            }}
                            disabled={updating}
                          >
                            <option value="">Select Language</option>
                            {programmingLanguages.map((lang) => (
                              <option key={lang} value={lang}>
                                {lang}
                              </option>
                            ))}
                          </select>

                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            defaultValue={task.technical_category || ''}
                            onChange={(e) => {
                              const newCategory = e.target.value;
                              const currentLanguage = task.programming_language || programmingLanguages[0];
                              updateTechnicalInfo(task.id, currentLanguage, newCategory);
                            }}
                            disabled={updating}
                          >
                            <option value="">Select Category</option>
                            {technicalCategories.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default TaskHistory;